import React from 'react';
import { useAuth } from '../App';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  User, 
  Briefcase, 
  Bookmark, 
  FileText, 
  MessageSquare, 
  LogOut,
  Users,
  PlusCircle,
  Menu,
  X,
  Megaphone,
  BadgeCheck,
  Heart,
  Shield,
  Eye,
  BarChart3
} from 'lucide-react';

export default function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  const isResearcher = user?.role === 'researcher';
  const isAdmin = user?.role === 'admin';
  const themeClass = isAdmin ? '' : isResearcher ? 'theme-researcher' : 'theme-client';
  const primaryColor = isAdmin ? '#6366f1' : isResearcher ? '#FF6B35' : '#0D7377';
  
  const researcherLinks = [
    { path: '/researcher/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/researcher/profile', label: 'My Profile', icon: User },
    { path: '/researcher/opportunities', label: 'Browse Opportunities', icon: Briefcase, section: 'discover' },
    { path: '/researcher/briefs', label: 'Browse Briefs', icon: Megaphone },
    { path: '/researcher/applications', label: 'My Applications', icon: FileText, section: 'activity' },
    { path: '/researcher/interests', label: 'My Interests', icon: Heart },
    { path: '/researcher/saved', label: 'Saved', icon: Bookmark },
    { path: '/researcher/messages', label: 'Messages', icon: MessageSquare, section: 'connect' },
    { path: '/researcher/verification', label: 'Get Verified', icon: BadgeCheck },
  ];
  
  const clientLinks = [
    { path: '/client/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/client/opportunities', label: 'My Opportunities', icon: Briefcase },
    { path: '/client/opportunities/new', label: 'Post Opportunity', icon: PlusCircle },
    { path: '/client/briefs', label: 'My Briefs', icon: Megaphone },
    { path: '/client/briefs/new', label: 'Post Brief', icon: PlusCircle },
    { path: '/client/profiles', label: 'Browse Researchers', icon: Users },
    { path: '/client/messages', label: 'Messages', icon: MessageSquare },
  ];
  
  const adminLinks = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/admin/moderation', label: 'Moderation', icon: Eye },
    { path: '/admin/verifications', label: 'Verifications', icon: Shield },
  ];
  
  const links = isAdmin ? adminLinks : isResearcher ? researcherLinks : clientLinks;
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <div className={`min-h-screen bg-neutral-100 ${themeClass}`}>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 header-glass px-4 py-3 flex items-center justify-between">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md hover:bg-neutral-100"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h1 className="font-bold text-lg" style={{ fontFamily: 'Outfit' }}>Research Exchange</h1>
        <div className="w-10" />
      </header>
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-neutral-200 z-40
        transform transition-transform duration-300 lg:translate-x-0 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-neutral-200">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div 
              className="w-8 h-8 rounded-sm flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: primaryColor }}
            >
              RE
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: 'Outfit' }}>
              Research Exchange
            </span>
          </Link>
        </div>
        
        {/* User Info */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: primaryColor }}
              >
                {user?.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{user?.name}</p>
              <span 
                className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                  isResearcher ? 'badge-researcher' : 'badge-client'
                }`}
              >
                {user?.role}
              </span>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-1">
            {links.map((link, index) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              const showDivider = link.section && index > 0;
              return (
                <React.Fragment key={link.path}>
                  {showDivider && (
                    <div className="pt-3 pb-1 px-3">
                      <div className="border-t border-neutral-100" />
                    </div>
                  )}
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium
                      transition-colors duration-200
                      ${isActive 
                        ? 'text-white' 
                        : 'text-neutral-600 hover:bg-neutral-100'
                      }
                    `}
                    style={isActive ? { backgroundColor: primaryColor } : {}}
                  >
                    <Icon size={18} />
                    {link.label}
                  </Link>
                </React.Fragment>
              );
            })}
          </div>
        </nav>
        
        {/* Logout */}
        <div className="p-4 border-t border-neutral-200 flex-shrink-0">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-neutral-600 hover:bg-neutral-100 w-full transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>
      
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {title && (
            <h1 
              className="text-2xl lg:text-3xl font-bold mb-6"
              style={{ fontFamily: 'Outfit' }}
            >
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
